import { useTranslation } from 'react-i18next';
import {
  useBackgroundScrollLock,
  useGamesGridConfig,
  useGetCombinedGames,
  useInfiniteScroll,
  useSearch
} from 'react-easyrocket';
import NoGames from 'src/assets/images/plugs/noSearchGames.webp';
import { CloseSearchTopbar } from 'src/components/svg/Closes';
import { SearchIcon } from 'src/components/svg/Searches';
import { GamesGrid } from 'src/components/Games/GamesGrid';
import { InputSearch } from 'src/components/Inputs/InputSearch/InputSearch';
import { useGameCardSize } from 'src/hooks/useGameCardSize';
import { useDrawer } from 'src/hooks/useDrawer';

export const TopbarSearch = () => {
  const { isGamesSearchDrawerOpen, closeDrawer } = useDrawer();
  const { value, onChange, debouncedValue, reset } = useSearch();
  const { columns } = useGamesGridConfig();
  useBackgroundScrollLock(isGamesSearchDrawerOpen, [isGamesSearchDrawerOpen]);

  const { t } = useTranslation();

  const enableRequest = Boolean(
    isGamesSearchDrawerOpen && debouncedValue && debouncedValue.length >= 2
  );

  const { games, fetchNextPage, hasNextPage, isFetched, isFetching } = useGetCombinedGames(
    // eslint-disable-next-line camelcase
    { name: debouncedValue, per_page: 12 },
    { categoryId: 'topbarSearch', enabled: enableRequest }
  );

  const { parentRef, childRef } = useInfiniteScroll({
    callback: () => {
      if (hasNextPage && value?.length >= 2) {
        fetchNextPage();
      }
    }
  });
  const gameCardSize = useGameCardSize(parentRef.current?.clientWidth, columns);
  const gamesFound = games?.length > 0;
  const gamesNotFound =
    games?.length === 0 && isGamesSearchDrawerOpen && value?.length !== 0 && isFetched;

  return (
    <div
      className="flex flex-col items-center self-center fixed top-0 bottom-0 z-[700] transition-all w-full h-screen bg-gs_bg"
      style={{
        transform: isGamesSearchDrawerOpen ? 'translateY(0px)' : 'translateY(-100%)'
      }}
      ref={parentRef}>
      <CloseSearchTopbar
        closeTopDrawer={() => {
          closeDrawer();
          reset();
        }}
      />
      <div className="mx-[20px] w-[90%]">
        <InputSearch
          placeholder={t('general.searchGamePlaceholder')}
          value={value}
          onChange={onChange}
          leftIcon={<SearchIcon className="r_sm:w-[20px] r_sm:h-[20px]" />}
          classNameInput="search-input text-[14px] text-gs_search_text font-medium bg-gs_bg w-full px-2.5 placeholder:text-i_placeholder"
          className="flex flex-row items-center bg-gs_bg w-full h-auto py-[5px] border-b-2 border-b-gs_search_border justify-around"
        />
      </div>
      {isGamesSearchDrawerOpen && gamesNotFound && (
        <div className="flex flex-col w-full items-center justify-center text-center px-[15px] pt-[15px]">
          <p className="text-gs_search_text m-auto">{t('gamesPage.noGamesInCategory')}</p>
          <img src={NoGames} alt="no games" className="w-auto" />
        </div>
      )}
      {isGamesSearchDrawerOpen && gamesFound && (
        <>
          <p className="pt-[20px] pb-2.5 font-bold text-center text-[20px] text-gs_search_text">
            {t('general.searchResults')}
          </p>
          <div className="overflow-y-auto">
            <GamesGrid
              isFetched={isFetched}
              isFetching={isFetching}
              className="px-2.5 pb-10"
              games={games}
              gameCardProps={gameCardSize}
            />
            {hasNextPage && !isFetching && <div className="h-[20px] w-full" ref={childRef}></div>}
          </div>
        </>
      )}
    </div>
  );
};
